import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Wrap,
  WrapItem,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const MoreIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.4C14.8,12.8 13.4,14.4 12,14.4C10.6,14.4 9.2,12.8 9.2,11.4V10C9.2,8.6 10.6,7 12,7Z" />
  </svg>
);

interface TeamRole {
  id: string;
  team_id: string;
  name: string;
  description?: string;
  permissions: string[];
  created_at: string;
  updated_at: string;
  is_default?: boolean;
  members_count?: number;
}

interface TeamRolesTabProps {
  teamId: string;
  permissions: {
    can_view_team: boolean;
    can_manage_roles: boolean;
    can_create_role: boolean;
    can_update_role: boolean;
    can_delete_role: boolean;
  };
  isActive: boolean;
  onEditRole?: (role: TeamRole) => void;
  onDeleteRole?: (roleId: string) => void;
}

export const TeamRolesTab: React.FC<TeamRolesTabProps> = ({
  teamId,
  permissions,
  isActive,
  onEditRole,
  onDeleteRole,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [roles, setRoles] = useState<TeamRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load roles data only when tab becomes active
  const loadRoles = async () => {
    if (!teamId || !permissions.can_view_team) return;

    try {
      setIsLoading(true);
      setError('');
      
      const rolesData = await teamService.getTeamRoles(teamId);
      setRoles(rolesData || []);
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team roles:', err);
      setError(err.message || 'Failed to load team roles');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team roles',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_team) {
      loadRoles();
    }
  }, [isActive, hasLoaded, permissions.can_view_team, teamId]);

  // Get role badge color
  const getRoleBadgeColor = (role: TeamRole) => {
    if (role.is_default) return 'blue';
    if (role.name?.toLowerCase().includes('admin')) return 'purple';
    if (role.name?.toLowerCase().includes('moderator')) return 'green';
    return 'gray';
  };

  // Get permission badge color
  const getPermissionBadgeColor = (permission: string) => {
    if (permission.includes('admin') || permission.includes('manage')) return 'red';
    if (permission.includes('create') || permission.includes('update') || permission.includes('delete')) return 'orange';
    if (permission.includes('view') || permission.includes('list')) return 'blue';
    return 'gray';
  };

  // Handle edit role
  const handleEditRole = (role: TeamRole) => {
    if (!permissions.can_update_role) return;
    if (onEditRole) {
      onEditRole(role);
    }
  };

  // Handle delete role
  const handleDeleteRole = async (role: TeamRole) => {
    if (!permissions.can_delete_role) return;
    
    if (role.is_default) {
      toast({
        title: t('common.error'),
        description: 'Cannot delete default role',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      try {
        if (onDeleteRole) {
          await onDeleteRole(role.id);
          await loadRoles(); // Reload after deletion
        }
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  // Format permission name for display
  const formatPermissionName = (permission: string) => {
    return permission
      .replace(/^perm_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Permission check
  if (!permissions.can_view_team) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewRoles')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingRoles')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadRoles} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && roles.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noRoles')}
        </Text>
        {permissions.can_create_role && (
          <Text color={textColor} fontSize="sm" mt={2}>
            {t('teams.createFirstRole')}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {t('teams.teamRoles')} ({roles.length})
        </Text>
        <Button
          size="sm"
          onClick={loadRoles}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Roles Grid */}
      <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={4}>
        {roles.map((role) => (
          <GridItem key={role.id}>
            <Card
              bg={cardBg}
              borderWidth={1}
              borderColor={borderColor}
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                shadow: 'md',
                borderColor: `${colorSchemes.primary}.300`,
              }}
            >
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  {/* Header with role name and actions */}
                  <HStack justify="space-between" align="start">
                    <HStack spacing={3}>
                      <ShieldIcon />
                      <VStack align="start" spacing={0}>
                        <HStack spacing={2}>
                          <Text fontWeight="semibold" fontSize="lg">
                            {role.name}
                          </Text>
                          <Badge
                            colorScheme={getRoleBadgeColor(role)}
                            variant="subtle"
                            size="sm"
                          >
                            {role.is_default ? t('teams.defaultRole') : t('teams.customRole')}
                          </Badge>
                        </HStack>
                        {role.description && (
                          <Text fontSize="sm" color={textColor}>
                            {role.description}
                          </Text>
                        )}
                      </VStack>
                    </HStack>

                    {/* Actions Menu */}
                    {permissions.can_manage_roles && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<MoreIcon />}
                          size="sm"
                          variant="ghost"
                        />
                        <MenuList>
                          {permissions.can_update_role && (
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => handleEditRole(role)}
                            >
                              {t('teams.editRole')}
                            </MenuItem>
                          )}
                          {permissions.can_delete_role && !role.is_default && (
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.500"
                              onClick={() => handleDeleteRole(role)}
                            >
                              {t('teams.deleteRole')}
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    )}
                  </HStack>

                  {/* Members count */}
                  {typeof role.members_count === 'number' && (
                    <HStack spacing={2}>
                      <Text fontSize="sm" color={textColor}>
                        {t('teams.membersWithRole')}:
                      </Text>
                      <Badge colorScheme="blue" variant="outline" size="sm">
                        {role.members_count}
                      </Badge>
                    </HStack>
                  )}

                  {/* Permissions */}
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      {t('teams.permissions')} ({role.permissions?.length || 0})
                    </Text>
                    {role.permissions && role.permissions.length > 0 ? (
                      <Wrap spacing={1}>
                        {role.permissions.slice(0, 6).map((permission) => (
                          <WrapItem key={permission}>
                            <Tooltip label={permission} hasArrow>
                              <Badge
                                colorScheme={getPermissionBadgeColor(permission)}
                                variant="outline"
                                size="xs"
                                cursor="help"
                              >
                                {formatPermissionName(permission)}
                              </Badge>
                            </Tooltip>
                          </WrapItem>
                        ))}
                        {role.permissions.length > 6 && (
                          <WrapItem>
                            <Badge
                              colorScheme="gray"
                              variant="solid"
                              size="xs"
                            >
                              +{role.permissions.length - 6} more
                            </Badge>
                          </WrapItem>
                        )}
                      </Wrap>
                    ) : (
                      <Text fontSize="xs" color={textColor}>
                        {t('teams.noPermissions')}
                      </Text>
                    )}
                  </VStack>

                  {/* Dates */}
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="xs" color={textColor}>
                      {t('teams.createdOn')}: {new Date(role.created_at).toLocaleDateString()}
                    </Text>
                    {role.updated_at !== role.created_at && (
                      <Text fontSize="xs" color={textColor}>
                        {t('teams.updatedOn')}: {new Date(role.updated_at).toLocaleDateString()}
                      </Text>
                    )}
                  </VStack>

                  {/* Action buttons */}
                  {permissions.can_manage_roles && (
                    <HStack spacing={2} pt={2}>
                      {permissions.can_update_role && (
                        <Button
                          size="sm"
                          colorScheme={colorSchemes.primary}
                          variant="outline"
                          leftIcon={<EditIcon />}
                          onClick={() => handleEditRole(role)}
                          flex={1}
                        >
                          {t('teams.edit')}
                        </Button>
                      )}
                      {permissions.can_delete_role && !role.is_default && (
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          leftIcon={<DeleteIcon />}
                          onClick={() => handleDeleteRole(role)}
                        >
                          {t('teams.delete')}
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
}; 